import { memo, useMemo } from 'react'
import type { ToolCall } from '../../../../types'
import { BUILTIN_TOOLS } from '../../../../integrations/utils'
import { AnimatePresence, motion, type Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { RemindersRenderer } from './reminders-renderer'
import { parseToolCallResultCached } from '../tools/tool-call-utils'
import { extractNestedToolName } from '../tools/tool-view'

const fadeInInitial = { opacity: 0 }
const fadeInAnimate = { opacity: 1 }
const fadeInTransition: Transition = { duration: 0.3 }

interface RemindersSectionProps {
  toolCalls: Array<ToolCall>
  groupToolCalls?: Array<ToolCall>
  isTurnActive: boolean
  onReminderClick: (reminderId: string) => void
}

export const RemindersSection = memo(function RemindersSection({
  toolCalls,
  groupToolCalls,
  isTurnActive,
  onReminderClick,
}: RemindersSectionProps) {
  const allToolCalls = useMemo(() => [...toolCalls, ...(groupToolCalls || [])], [toolCalls, groupToolCalls])

  const reminderToolCalls = useMemo(
    () =>
      allToolCalls.filter(
        (toolCall) =>
          toolCall.toolName === BUILTIN_TOOLS.CREATE_REMINDER ||
          (toolCall.toolName === 'other' && extractNestedToolName(toolCall) === BUILTIN_TOOLS.CREATE_REMINDER),
      ),
    [allToolCalls],
  )

  if (reminderToolCalls.length === 0) return null

  return (
    <>
      {reminderToolCalls.map((toolCall, index) => {
        const isFailed = toolCall.status === 'failed'
        const isToolLoading = toolCall.status === 'executing' || toolCall.status === 'ready'
        const isLast = index === reminderToolCalls.length - 1

        if (isToolLoading || isTurnActive) return null

        if (isFailed) {
          return (
            <AnimatePresence key={toolCall.id}>
              <motion.div
                initial={fadeInInitial}
                animate={fadeInAnimate}
                transition={fadeInTransition}
                className={twMerge('mt-2', isLast && 'mb-2')}
              >
                <div className="border-danger bg-danger-subtle text-danger-subtle rounded-lg border p-3 text-sm">
                  Failed to create reminder
                </div>
              </motion.div>
            </AnimatePresence>
          )
        }

        const result = parseToolCallResultCached(toolCall)
        if (!result?.id) return null

        return (
          <AnimatePresence key={toolCall.id}>
            <motion.div initial={fadeInInitial} animate={fadeInAnimate} transition={fadeInTransition}>
              <RemindersRenderer
                reminders={[result]}
                isLast={isLast}
                onReminderClick={onReminderClick}
                view={toolCall.view}
              />
            </motion.div>
          </AnimatePresence>
        )
      })}
    </>
  )
})
